/**
 * The light game chrome (ui-map-interface.md §2.5 "The chrome", R3298).
 *
 * On a page in the light identity, the root of the game chrome (`#ui`)
 * wears the light body of the UI theme in force and carries
 * `data-identity="light"`, so the light block of `gen/tokens.css` (the
 * frozen inks, the map inks the DOM repeats and the panel shadow) reaches
 * its subtree. Every other root of a page (the hub frame, a cover, a lab
 * root) wears the same light body through `adoptLightRoot` (R3320): every
 * root of a page wears the light body. Only the chrome root carries the
 * seat team on `--team`.
 *
 * balancegen computes the light body of AUTO, the five faction themes and
 * the vendored UI themes (`LIGHT_CHROME_FACTIONS`, `LIGHT_CHROME_VENDORED`
 * of `gen/inks.ts`). A custom UI theme takes the twin of the rule,
 * `lightChromeBody` of `ui/lightrule.ts`, once per body. In the dark
 * identity every function of this module does nothing.
 */

import { LIGHT } from '../identity/pick';
import {
  LIGHT_CHROME_FACTIONS, LIGHT_CHROME_RULE, LIGHT_CHROME_VENDORED, type ChromeBody,
} from '../gen/inks';
import { FACTIONS, factionOf } from '../world';
import { hex } from '../render/color';
import { FALLBACK_FACTION, bodyTokens, rgbOfHex, type ThemeBody } from './theme';
import { lightChromeBody, type ChromeInks } from './lightrule';

/** The `data-identity` value of a chrome root in the light identity. */
export const LIGHT_IDENTITY = 'light';

/** The seven inks a body states, the dim aside: two bodies with the same
 *  seven are one theme. */
const KEYS = ['accent', 'text', 'faint', 'panel', 'sheet', 'ink', 'deep'] as const;

/** The light bodies the twin computed in this page load, by dark body. */
const computed = new Map<string, ChromeBody>();

/** The light body of faction `id`; an id outside the roster takes the
 *  base faction. */
export function lightFactionBody(id: number): ChromeBody {
  const row = LIGHT_CHROME_FACTIONS.find((r) => r.id === String(id))
    ?? LIGHT_CHROME_FACTIONS.find((r) => r.id === String(FALLBACK_FACTION))!;
  return row.light;
}

/**
 * The light body of one dark body. A body of a theme the build ships (a
 * faction theme or a vendored one) takes its generated light body; any
 * other body, a custom theme, takes the twin of the rule with its own
 * accent.
 */
export function lightBodyFor(dark: ChromeInks): ChromeBody {
  const same = (r: { dark: ChromeBody }) => KEYS.every((k) => r.dark[k] === dark[k]);
  const row = LIGHT_CHROME_FACTIONS.find(same) ?? LIGHT_CHROME_VENDORED.find(same);
  if (row) return row.light;
  const key = KEYS.map((k) => dark[k]).join(',');
  let body = computed.get(key);
  if (!body) {
    body = lightChromeBody(LIGHT_CHROME_RULE, dark, dark.accent);
    computed.set(key, body);
  }
  return body;
}

/** The custom properties one light body writes: the tokens of `bodyTokens`
 *  with the edge in the ink of `[light.chrome] edge`. */
export function lightTokens(b: ThemeBody): Record<string, string> {
  return { ...bodyTokens(b), '--edge': `rgb(var(--${LIGHT_CHROME_RULE.edge.ink}-rgb) / var(--edge-alpha))` };
}

/** Writes one light body onto one element and marks it light. The element
 *  and its subtree wear the light chrome; nothing else changes. */
export function wearLightOn(el: HTMLElement, b: ThemeBody): void {
  el.dataset.identity = LIGHT_IDENTITY;
  for (const [name, value] of Object.entries(lightTokens(b))) el.style.setProperty(name, value);
}

/** The root of the game chrome, once a page adopts it. */
let root: HTMLElement | null = null;
/** The other roots of the page that wear the light body (R3320). */
const roots = new Set<HTMLElement>();
/** The light body in force: the base faction's until a theme is worn. */
let worn: ChromeBody = lightFactionBody(FALLBACK_FACTION);
/** The seat team of an arcade match or a record, 0 for none. */
let seat = 0;

/** Adopts the root of the game chrome (`#ui`): in the light identity it
 *  wears the light body in force from now on. */
export function adoptChromeRoot(el: HTMLElement): void {
  if (!LIGHT) return;
  root = el;
  wearLightOn(el, worn);
  writeTeam();
}

/**
 * Adopts one more root of the page (R3320): in the light identity it
 * wears the light body in force now, and again on every later theme
 * change. A root that leaves the page for good calls `releaseLightRoot`.
 * The seat team stays on the chrome root alone.
 */
export function adoptLightRoot(el: HTMLElement): void {
  if (!LIGHT) return;
  roots.add(el);
  wearLightOn(el, worn);
}

/** Forgets one root of `adoptLightRoot`; its light body stays written. */
export function releaseLightRoot(el: HTMLElement): void {
  roots.delete(el);
}

/** Wears the light body in force on the chrome root and every root. */
function wearAll(): void {
  if (root) wearLightOn(root, worn);
  for (const el of roots) wearLightOn(el, worn);
}

/** Wears the light body of one dark UI theme body on every root. */
export function wearLightTheme(dark: ThemeBody): void {
  if (!LIGHT) return;
  worn = lightBodyFor(dark);
  wearAll();
}

/** Wears the light body of a faction theme on every root. */
export function wearLightFaction(id: number): void {
  if (!LIGHT) return;
  worn = lightFactionBody(id);
  wearAll();
}

/**
 * The light half of `wearSeatTeam` (R2051): the neutral chrome, and the
 * seat's team on `--team` in the light faction ink of the map, because
 * the team is an identity mark and the map draws the team in that ink.
 */
export function wearLightSeat(team: number): void {
  if (!LIGHT) return;
  wearLightFaction(FALLBACK_FACTION);
  wearLightTeam(team);
}

/** The light half of `wearTeamInk`: a team under 1 takes `--team` away. */
export function wearLightTeam(team: number): void {
  if (!LIGHT) return;
  seat = team;
  writeTeam();
}

function writeTeam(): void {
  if (!root) return;
  const f = seat >= 1 ? factionOf(seat) ?? FACTIONS[0] : null;
  if (f === null) {
    root.style.removeProperty('--team');
    root.style.removeProperty('--team-rgb');
    return;
  }
  const c = hex(f.color);
  root.style.setProperty('--team', c);
  root.style.setProperty('--team-rgb', rgbOfHex(c).join(' '));
}

/** Forgets the chrome root, the other roots and the body in force.
 *  Tests only. */
export function resetLightChrome(): void {
  root = null;
  roots.clear();
  worn = lightFactionBody(FALLBACK_FACTION);
  seat = 0;
  computed.clear();
}
